import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Textarea,
  Button,
  Alert,
  Box,
  Spinner,
} from '@cloudscape-design/components';
import { JudgeCriteria } from '../types';
import { apiService } from '../services/ApiService';

export const JudgePanel: React.FC = () => {
  const [criteria, setCriteria] = useState<Record<string, JudgeCriteria>>({});
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadCriteria();
  }, []);

  const loadCriteria = async () => {
    try {
      setLoading(true);
      const data = await apiService.getJudgeCriteria();
      setCriteria(data);
      const edits: Record<string, string> = {};
      for (const [key, val] of Object.entries(data)) {
        edits[key] = val.criteria;
      }
      setEditValues(edits);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load criteria');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (testId: string) => {
    try {
      setError(null);
      setSuccess(null);
      setSaving(testId);
      await apiService.updateJudgeCriteria(parseInt(testId), editValues[testId]);
      setSuccess(`Criteria for "${criteria[testId].testName}" updated`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    );
  }

  const sortedKeys = Object.keys(criteria).sort((a, b) => parseInt(a) - parseInt(b));

  return (
    <SpaceBetween size="l">
      <Header variant="h2" description="Configure the rating prompt each model sees when judging other models' responses">
        Judge Criteria
      </Header>
      {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" dismissible onDismiss={() => setSuccess(null)}>{success}</Alert>}
      {sortedKeys.map((key) => {
        const item = criteria[key];
        const changed = editValues[key] !== item.criteria;
        return (
          <Container
            key={key}
            header={<Header variant="h3">Test {item.testId}: {item.testName}</Header>}
          >
            <SpaceBetween size="s">
              <Textarea
                value={editValues[key] || ''}
                onChange={({ detail }) =>
                  setEditValues((prev) => ({ ...prev, [key]: detail.value }))
                }
                rows={3}
              />
              <Button
                onClick={() => handleSave(key)}
                loading={saving === key}
                disabled={!changed || saving !== null}
              >
                Save
              </Button>
            </SpaceBetween>
          </Container>
        );
      })}
    </SpaceBetween>
  );
};
